import { useState } from 'react';
import { Check, Minus, Plus, Info } from 'lucide-react';
import { formatMoney } from '../../utils/format';

/**
 * OptionalItemsTable - Checkbox list for optional add-on items
 * Supports quantity adjustment for items with quantity_editable
 */
export default function OptionalItemsTable({
  items,
  selectedItems,
  customQuantities,
  onToggleItem,
  onUpdateQuantity,
  disabled = false
}) {
  const [expandedItem, setExpandedItem] = useState(null);

  if (!items || items.length === 0) {
    return null;
  }

  const formatPrice = (price) => {
    return formatMoney(parseFloat(price));
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Optional Add-ons</h3>

      <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
        {items.map((item) => {
          const isSelected = selectedItems.includes(item.id);
          const quantity = customQuantities[item.id] ?? item.quantity;
          const itemTotal = parseFloat(item.unit_price) * quantity;
          const isExpanded = expandedItem === item.id;

          return (
            <div key={item.id} className="py-4 first:pt-0 last:pb-0">
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onToggleItem(item.id)}
                  className={`
                    mt-1 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-all
                    ${isSelected
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'border-neutral-300 dark:border-neutral-500 hover:border-primary-400 dark:hover:border-primary-500'
                    }
                    ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {isSelected && <Check className="w-3 h-3" />}
                </button>

                {/* Item details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className={`font-medium ${isSelected ? 'text-neutral-900 dark:text-white' : 'text-neutral-600 dark:text-neutral-400'}`}>
                        {item.product_name}
                      </h4>
                      {item.description && (
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{item.description}</p>
                      )}
                      {item.long_description && (
                        <button
                          type="button"
                          onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-1 flex items-center gap-1"
                        >
                          <Info className="w-3 h-3" />
                          {isExpanded ? 'Hide details' : 'Show details'}
                        </button>
                      )}
                    </div>

                    {/* Price */}
                    <div className="text-right flex-shrink-0">
                      <div className={`font-semibold ${isSelected ? 'text-neutral-900 dark:text-white' : 'text-neutral-500 dark:text-neutral-400'}`}>
                        {formatPrice(item.unit_price)}
                        {item.is_recurring && (
                          <span className="text-xs text-neutral-500 dark:text-neutral-400 font-normal">
                            /{item.recurring_cycle || 'mo'}
                          </span>
                        )}
                      </div>
                      {quantity > 1 && isSelected && (
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">
                          Total: {formatPrice(itemTotal)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded description */}
                  {isExpanded && item.long_description && (
                    <div className="mt-3 p-3 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg text-sm text-neutral-600 dark:text-neutral-300">
                      {item.long_description}
                    </div>
                  )}

                  {/* Quantity controls */}
                  {item.quantity_editable && isSelected && (
                    <div className="mt-3 flex items-center gap-3">
                      <span className="text-sm text-neutral-600 dark:text-neutral-400">Quantity:</span>
                      <div className="flex items-center border border-neutral-300 dark:border-neutral-600 rounded-lg">
                        <button
                          type="button"
                          disabled={disabled || quantity <= (item.min_quantity || 1)}
                          onClick={() => onUpdateQuantity(item.id, Math.max(item.min_quantity || 1, quantity - 1))}
                          className="p-2 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="px-4 py-2 min-w-[3rem] text-center font-medium text-neutral-900 dark:text-white">
                          {quantity}
                        </span>
                        <button
                          type="button"
                          disabled={disabled || (item.max_quantity && quantity >= item.max_quantity)}
                          onClick={() => onUpdateQuantity(item.id, quantity + 1)}
                          className="p-2 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      {item.min_quantity && (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">Min: {item.min_quantity}</span>
                      )}
                      {item.max_quantity && (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">Max: {item.max_quantity}</span>
                      )}
                    </div>
                  )}

                  {/* Recurring badge */}
                  {item.is_recurring && (
                    <span className="inline-block mt-2 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                      Recurring {item.recurring_cycle}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
